import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { CountryLabel } from '../../../shared/components/CountryFlag';
import {
    Card,
    Row,
    Col,
    Statistic,
    Select,
    Space,
    Typography,
    Table,
    Empty,
    Spin,
    message,
    Divider,
    DatePicker
} from 'antd';
import {
    LineChartOutlined,
    ThunderboltOutlined,
    CloudServerOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import dayjs from 'dayjs';
import { retentionDisabledDate, clampRange, RetentionHint } from '../../../shared/components/cdn/PlanGate';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Analytics = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(7, 'day'),
        dayjs()
    ]);
    // 套餐日志保留天数(接口返回 retention_days;全部域名时取最长,各域名在服务端按自己的套餐截断)
    const [retDays, setRetDays] = useState(null);

    // User filters
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [domains, setDomains] = useState([]);
    const [domainSearch, setDomainSearch] = useState('');

    const [stats, setStats] = useState({
        traffic: [],
        status: [],
        cache: [],
        topUrls: [],
        topDomains: [],
        browsers: [],
        totalRequests: 0,
        totalTraffic: 0,
        cacheHitRate: 0,
        avgResponseTime: 0
    });

    useEffect(() => {
        loadDomains();
    }, [domainSearch]);

    useEffect(() => {
        if (dateRange && dateRange[0] && dateRange[1]) {
            loadAllStats();
        }
    }, [dateRange, selectedDomain]);

    const loadDomains = async () => {
        try {
            const response = await apiClient.get('/api/user/cdn/domains');
            if (response.data.success) {
                let domainsData = response.data.data || [];

                // Client-side filtering by search text
                if (domainSearch) {
                    domainsData = domainsData.filter(d =>
                        d.domain.toLowerCase().includes(domainSearch.toLowerCase())
                    );
                }

                setDomains(domainsData);
            }
        } catch (error) {
            console.error('Failed to load domains:', error);
        }
    };

    const loadAllStats = async () => {
        setLoading(true);
        try {
            const startTime = dateRange[0].unix(); // 发 Unix 秒:服务端按 Asia/Shanghai 解析字符串,非东八区用户会偏移时间窗
            const endTime = dateRange[1].unix();
            const params = { startTime, endTime };
            if (selectedDomain) {
                params.domain_id = selectedDomain;
            }

            const [statsRes, mapRes, cacheRes, topUrlsRes, topDomainsRes, statusRes, browsersRes] = await Promise.all([
                apiClient.get('/api/user/traffic/stats', { params }),
                apiClient.get('/api/user/traffic/map', { params }),
                apiClient.get('/api/user/traffic/cache', { params }),
                apiClient.get('/api/user/traffic/top-urls', { params }),
                apiClient.get('/api/user/traffic/top-domains', { params }),
                apiClient.get('/api/user/traffic/status-codes', { params }),
                apiClient.get('/api/user/traffic/browsers', { params })
            ]);

            if (statsRes.data?.retention_days) setRetDays(Number(statsRes.data.retention_days));
            setStats({
                totalRequests: statsRes.data.success ? (statsRes.data.data.totalRequests || statsRes.data.data.total_requests || 0) : 0,
                totalTraffic: statsRes.data.success ? (statsRes.data.data.totalTraffic || statsRes.data.data.total_traffic || 0) : 0,
                cacheHitRate: statsRes.data.success ? (statsRes.data.data.cacheHitRate || statsRes.data.data.cache_hit_rate || 0) : 0,
                avgResponseTime: statsRes.data.success ? (statsRes.data.data.avgResponseTime || statsRes.data.data.avg_response_time || 0) : 0,
                traffic: mapRes.data.success ? mapRes.data.data || [] : [],
                cache: cacheRes.data.success ? cacheRes.data.data || [] : [],
                topUrls: topUrlsRes.data.success ? topUrlsRes.data.data || [] : [],
                topDomains: topDomainsRes.data.success ? topDomainsRes.data.data || [] : [],
                status: statusRes.data.success ? statusRes.data.data || [] : [],
                browsers: browsersRes.data.success ? browsersRes.data.data || [] : []
            });
        } catch (error) {
            message.error(t('Failed to load statistics: {{value}}', { value: error.response?.data?.error || error.message }));
        } finally {
            setLoading(false);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('zh-CN').format(num || 0);
    };

    const trafficColumns = [
        {
            title: t('Country/Region'),
            dataIndex: 'country_name',
            key: 'country_name',
            render: (text, record) => (text || record.country_code ? <CountryLabel code={record.country_code}>{text || record.country_code}</CountryLabel> : '-')
        },
        {
            title: t('Traffic'),
            dataIndex: 'traffic',
            key: 'traffic',
            render: (value) => formatBytes(value),
            sorter: (a, b) => a.traffic - b.traffic
        },
        {
            title: t('Requests'),
            dataIndex: 'requests',
            key: 'requests',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.requests - b.requests
        }
    ];

    const urlColumns = [
        {
            title: 'URL',
            dataIndex: 'url',
            key: 'url',
            ellipsis: true
        },
        {
            title: t('Visits'),
            dataIndex: 'hits',
            key: 'hits',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.hits - b.hits
        }
    ];

    const statusColumns = [
        {
            title: t('HTTP status code'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'default';
                if (status >= 200 && status < 300) color = 'success';
                else if (status >= 300 && status < 400) color = 'processing';
                else if (status >= 400 && status < 500) color = 'warning';
                else if (status >= 500) color = 'error';
                return <Text type={color === 'success' ? 'success' : color === 'error' ? 'danger' : undefined}>{status}</Text>;
            }
        },
        {
            title: t('Count'),
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const browserColumns = [
        {
            title: t('Browser'),
            dataIndex: 'browser',
            key: 'browser'
        },
        {
            title: t('Visits'),
            dataIndex: 'count',
            key: 'count',
            render: (value) => formatNumber(value),
            sorter: (a, b) => a.count - b.count
        }
    ];

    const domainColumns = [
        {
            title: t('Domains'),
            dataIndex: 'domain',
            key: 'domain',
            ellipsis: true
        },
        {
            title: t('Traffic'),
            dataIndex: 'traffic',
            key: 'traffic',
            render: (value) => formatBytes(value),
            sorter: (a, b) => (a.traffic || 0) - (b.traffic || 0)
        },
        {
            title: t('Requests'),
            dataIndex: 'requests',
            key: 'requests',
            render: (value) => formatNumber(value),
            sorter: (a, b) => (a.requests || 0) - (b.requests || 0)
        },
        {
            title: t('Cache hit ratio'),
            dataIndex: 'cache_hit_rate',
            key: 'cache_hit_rate',
            render: (value) => `${(value || 0).toFixed(1)}%`,
            sorter: (a, b) => (a.cache_hit_rate || 0) - (b.cache_hit_rate || 0)
        }
    ];

    return (
        <div>
            <Card
                title={t('CDN traffic analytics')}
                extra={
                    <Space>
                        <Select
                            showSearch
                            allowClear
                            style={{ width: 250 }}
                            placeholder={t('Filter domains (all by default)')}
                            value={selectedDomain}
                            onChange={setSelectedDomain}
                            onSearch={setDomainSearch}
                            filterOption={false}
                            notFoundContent={loading ? <Spin size="small" /> : <Empty description={t('No data')} />}
                        >
                            {domains.map(domain => (
                                <Option key={domain.id} value={domain.id}>
                                    <GlobalOutlined /> {domain.domain}
                                </Option>
                            ))}
                        </Select>
                        <RangePicker
                            value={dateRange}
                            onChange={(r) => setDateRange(clampRange(r, retDays))}
                            disabledDate={retentionDisabledDate(retDays)}
                            showTime
                            format="YYYY-MM-DD HH:mm:ss"
                            placeholder={[t('Start time'), t('End time')]}
                        />
                        <RetentionHint days={retDays} />
                    </Space>
                }
            >
                <Spin spinning={loading}>
                    {/* 统计卡片 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Total traffic')}
                                    value={formatBytes(stats.totalTraffic)}
                                    prefix={<CloudServerOutlined />}
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Total requests')}
                                    value={formatNumber(stats.totalRequests)}
                                    prefix={<LineChartOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Cache hit ratio')}
                                    value={stats.cacheHitRate}
                                    suffix="%"
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{ color: stats.cacheHitRate > 80 ? '#3f8600' : '#cf1322' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card>
                                <Statistic
                                    title={t('Countries/regions covered')}
                                    value={stats.traffic.filter(r => r.country_code).length}
                                    prefix={<GlobalOutlined />}
                                    valueStyle={{ color: '#722ed1' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Divider />

                    {/* 热门域名 */}
                    <Card title={t('Popular domains')} size="small" style={{ marginBottom: 16 }}>
                        {stats.topDomains.length > 0 ? (
                            <Table
                                columns={domainColumns}
                                dataSource={stats.topDomains}
                                rowKey="domain"
                                pagination={{ pageSize: 5 }}
                                size="small"
                            />
                        ) : (
                            <Empty description={t('No data yet')} />
                        )}
                    </Card>

                    {/* 详细统计表格 */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card
                                title={t('Traffic by region')}
                                size="small"
                            >
                                {stats.traffic.length > 0 ? (
                                    <Table
                                        columns={trafficColumns}
                                        dataSource={stats.traffic}
                                        rowKey={(record) => `${record.country_code}-${record.country_name}`}
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title={t('Top URLs')}
                                size="small"
                            >
                                {stats.topUrls.length > 0 ? (
                                    <Table
                                        columns={urlColumns}
                                        dataSource={stats.topUrls}
                                        rowKey="url"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title={t('HTTP status code distribution')}
                                size="small"
                            >
                                {stats.status.length > 0 ? (
                                    <Table
                                        columns={statusColumns}
                                        dataSource={stats.status}
                                        rowKey="status"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>

                        <Col xs={24} lg={12}>
                            <Card
                                title={t('Browser distribution')}
                                size="small"
                            >
                                {stats.browsers.length > 0 ? (
                                    <Table
                                        columns={browserColumns}
                                        dataSource={stats.browsers}
                                        rowKey="browser"
                                        pagination={{ pageSize: 10 }}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t('No data yet')} />
                                )}
                            </Card>
                        </Col>
                    </Row>
                </Spin>
            </Card>
        </div>
    );
};

export default Analytics;
